import { Component, inject, viewChild, ElementRef, AfterViewInit, signal, computed, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveFormsModule, FormsModule, Validators, FormBuilder } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyRichTextEditorModule } from '@vendasta/galaxy/rich-text-editor';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { firstValueFrom } from 'rxjs';
import { 
  SendApiService,
  SendFromProviderRequest,
  Provider,
  UserParticipant,
  ContactParticipant,
  WhiteLabelEmailMessage
} from '@vendasta/email';
import { DEFAULT_RICH_TEXT_PLUGINS, DEFAULT_RICH_TEXT_TOOLBAR, EmailProvider, EMAIL_PROVIDER_GMAIL, EMAIL_PROVIDER_OUTLOOK } from './constants';

interface ContactInfo {
  id: string;
  name: string;
  email: string;
}

@Component({
  selector: 'lib-crm-send-email',
  templateUrl: './send-email.component.html',
  styleUrls: ['./send-email.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    GalaxyFormFieldModule,
    GalaxyRichTextEditorModule,
    GalaxyButtonLoadingIndicatorModule,
  ],
})
export class CrmSendEmailComponent implements AfterViewInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackbarService = inject(SnackbarService);
  private readonly fb = inject(FormBuilder);
  private readonly sendEmailService = inject(SendApiService);
  
  // Signal-based route query parameters
  private readonly queryParams = toSignal(this.route.queryParamMap);
  
  // Signal-based viewChild for programmatic focus
  readonly subjectInput = viewChild<ElementRef<HTMLInputElement>>('subjectInput');
  
  // Rich text editor constants
  protected readonly DEFAULT_RICH_TEXT_PLUGINS = DEFAULT_RICH_TEXT_PLUGINS;
  protected readonly DEFAULT_RICH_TEXT_TOOLBAR = DEFAULT_RICH_TEXT_TOOLBAR;
  
  // Signal-based reactive state
  protected readonly showErrors = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly isSending = signal(false);
  protected readonly contacts = signal<ContactInfo[]>([]);

  protected readonly emailForm = this.fb.nonNullable.group({
    to: ['', [Validators.required]],
    subject: ['', [Validators.required]],
    content: ['', [Validators.required]]
  });

  // Computed signals derived from query parameters
  private readonly currentNamespace = computed(() => {
    const params = this.queryParams();
    return params?.get('namespace') || '';
  });

  private readonly currentUserId = computed(() => {
    const params = this.queryParams();
    return params?.get('userId') || '';
  });

  private readonly returnUrl = computed(() => {
    const params = this.queryParams();
    return params?.get('returnUrl') || '';
  });

  private readonly contactIds = computed(() => {
    const params = this.queryParams();
    return params?.get('contacts') || null;
  });

  private readonly emailAddresses = computed(() => {
    const params = this.queryParams();
    return params?.get('emails') || null;
  });

  private readonly emailProvider = computed(() => {
    const params = this.queryParams();
    const provider = params?.get('provider') || EMAIL_PROVIDER_GMAIL;
    return provider as EmailProvider;
  });

  // Effect to load contacts when query parameters change
  constructor() {
    effect(() => {
      const contactIdsValue = this.contactIds();
      const emailAddressesValue = this.emailAddresses();
      
      if (contactIdsValue) {
        this.loadAndPopulateContacts(contactIdsValue, emailAddressesValue);
      }
    });
  }

  ngAfterViewInit() {
    // Focus on subject field after view initializes
    const inputElement = this.subjectInput();
    if (inputElement) {
      setTimeout(() => {
        inputElement.nativeElement.focus();
      }, 0);
    }
  }

  private async loadAndPopulateContacts(contactIds: string, emailAddresses: string | null): Promise<void> {
    try {
      this.isLoading.set(true);
      const ids = contactIds.split(',');
      const emails = emailAddresses ? emailAddresses.split(',') : [];
      
      // Build contact list with actual email addresses from CRM
      this.contacts.set(ids.map((id, index) => ({
        id,
        name: `Contact ${index + 1}`,
        email: emails[index] || ''
      })));
      
      // Pre-populate the "to" field with valid contact emails
      const validEmails = this.contacts()
        .map(contact => contact.email)
        .filter(email => email && email.trim() !== '');
      
      if (validEmails.length > 0) {
        this.emailForm.patchValue({
          to: validEmails.join(', ')
        });
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      this.snackbarService.openErrorSnack('CRM.ERROR_LOADING_CONTACTS');
    } finally {
      this.isLoading.set(false);
    }
  }

  protected async onSend() {
    if (!this.emailForm.valid) {
      this.showErrors.set(true);
      this.emailForm.markAllAsTouched();
      return;
    }
    if (!this.isSending()) {
      try {
        this.isSending.set(true);
        
        const formValue = this.emailForm.value;
        await this.sendEmailWithProvider(formValue.to, formValue.subject, formValue.content, this.emailProvider());
        this.snackbarService.openSuccessSnack('CRM.EMAIL_SENT_SUCCESS');
        // Navigate back to CRM
        this.goBack();
      } catch (error) {
        console.error('Error sending email:', error);
        this.snackbarService.openErrorSnack('CRM.ERROR_SENDING_EMAIL');
      } finally {
        this.isSending.set(false);
      }
    }
  }

  private async sendEmailWithProvider(to?: string, subject?: string, content?: string, provider?: EmailProvider): Promise<void> {
    try {
      // Parse recipients from the email data
      const toEmails = to?.split(',').map((email: string) => email.trim()) || [];
      
      // Send email to each recipient using sendFromProvider
      for (const recipientEmail of toEmails) {
        if (recipientEmail) {
          const request = this.buildSendFromProviderRequest(
            recipientEmail,
            this.currentNamespace(),
            this.currentUserId(),
            subject || '',
            content || '',
            provider as EmailProvider
          );
          await firstValueFrom(this.sendEmailService.sendFromProvider(request));
        }
      }
    } catch (error) {
      console.error('Failed to send email via sendFromProvider:', error);
      throw error;
    }
  }

  private buildSendFromProviderRequest(recipientEmail: string,namespace: string,currentUserId: string,
    subject: string,htmlContent: string, provider: EmailProvider): SendFromProviderRequest {
    // Extract contact ID from the contacts array if available
    const contact = this.contacts().find(c => c.email === recipientEmail);
    const contactId = contact?.id || recipientEmail;
    // Create UserParticipant instance
    const userParticipant = new UserParticipant({
      userId: currentUserId
    });
   // Create ContactParticipant instance
    const contactParticipant = new ContactParticipant({
      contactId: contactId,
      namespace: namespace,
    });
    // Create WhiteLabelEmailMessage instance
    const message = new WhiteLabelEmailMessage({
      subject: subject,
      htmlContent: htmlContent,
      attributes: [],
      links: [],
      attachments: [],
      urlAttachments: []
    });

    // Create and return the request
    return new SendFromProviderRequest({
      namespace: namespace,
      provider: provider === EMAIL_PROVIDER_OUTLOOK ? Provider.PROVIDER_OUTLOOK : Provider.PROVIDER_GMAIL,
      userParticipant: userParticipant,
      contactParticipant: contactParticipant,
      message: message,
      sendAsDraft: false,
      providerMessageThreadId: undefined
    });
  }

  onCancel() {
    this.goBack();
  }

  private goBack() {
    // Navigate back using returnUrl from query params
    this.router.navigateByUrl(this.returnUrl());
  }
}

